import { useEffect, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ReviewForm from '../components/reviews/ReviewForm'
import ReviewCard from '../components/reviews/ReviewCard'
import FormError from '../components/ui/FormError'
import {
  MY_BOOKINGS_QUERY,
  type CustomerBooking,
  type MyBookingsData,
  type MyBookingsVariables,
} from '../graphql/customer'
import {
  CREATE_REVIEW_MUTATION,
  type CreateReviewData,
  type CreateReviewVariables,
  type CreatedReview,
} from '../graphql/reviews'
import { useLang, useScopedTranslations } from '../contexts/LangContext'

const translations = {
  el: {
    loading: 'Φορτώνουμε τα στοιχεία αξιολόγησης...',
    notFoundTitle: 'Η κράτηση δεν είναι διαθέσιμη για αξιολόγηση',
    notFoundBody: 'Μπορείς να αξιολογήσεις μόνο ολοκληρωμένες κρατήσεις από τον λογαριασμό σου.',
    back: 'Επιστροφή στην κράτηση',
    title: 'Άφησε αξιολόγηση',
    body: 'Μίλησε για την εμπειρία σου ώστε να βοηθήσεις τους επόμενους πελάτες.',
    ratingLabel: 'Βαθμολογία',
    commentLabel: 'Σχόλιο',
    commentPlaceholder: 'Πες στους επόμενους πελάτες πώς πήγε το ραντεβού...',
    charCount: 'χαρακτήρες',
    submit: 'Υποβολή αξιολόγησης',
    poor: 'Κακή',
    belowAverage: 'Κάτω του μέσου όρου',
    average: 'Μέτρια',
    good: 'Καλή',
    excellent: 'Εξαιρετική',
    schedule: 'Ραντεβού',
    service: 'Υπηρεσία',
    property: 'Ακίνητο',
    summary: 'Σύνοψη πριν την αποστολή',
    noProperty: 'Χωρίς ακίνητο',
    commentPreviewEmpty: 'Δεν έχεις γράψει ακόμη σχόλιο.',
    validationRating: 'Επίλεξε βαθμολογία από 1 έως 5 αστέρια.',
    validationComment: 'Αν προσθέσεις σχόλιο, γράψε τουλάχιστον 20 χαρακτήρες.',
    successTitle: 'Ευχαριστούμε για την αξιολόγηση!',
    successBody: 'Η αξιολόγησή σου δημοσιεύτηκε και ενημέρωσε το δημόσιο προφίλ του επαγγελματία.',
    alreadyReviewed: 'Έχεις ήδη αφήσει αξιολόγηση για αυτή την κράτηση.',
    backToBookings: 'Επιστροφή στην κράτηση',
    redirecting: 'Επιστροφή σε λίγα δευτερόλεπτα...',
    reviewPlaceholder: 'Χωρίς γραπτό σχόλιο',
  },
  en: {
    loading: 'Loading review details...',
    notFoundTitle: 'This booking is not available for review',
    notFoundBody: 'You can only review completed bookings from your account.',
    back: 'Back to booking',
    title: 'Leave a review',
    body: 'Share your experience to help the next customer decide.',
    ratingLabel: 'Rating',
    commentLabel: 'Review',
    commentPlaceholder: 'Tell other customers how the appointment went...',
    charCount: 'characters',
    submit: 'Submit review',
    poor: 'Poor',
    belowAverage: 'Below average',
    average: 'Average',
    good: 'Good',
    excellent: 'Excellent',
    schedule: 'Schedule',
    service: 'Service',
    property: 'Property',
    summary: 'Summary before submit',
    noProperty: 'No property',
    commentPreviewEmpty: 'You have not written a comment yet.',
    validationRating: 'Choose a star rating from 1 to 5.',
    validationComment: 'If you add a comment, write at least 20 characters.',
    successTitle: 'Thank you for your review!',
    successBody: 'Your review is now published and reflected on the provider profile.',
    alreadyReviewed: 'You have already reviewed this booking.',
    backToBookings: 'Back to booking',
    redirecting: 'Returning in a few seconds...',
    reviewPlaceholder: 'No written comment',
  },
} as const

export default function BookingReviewPage() {
  const { id } = useParams()
  const { locale } = useLang()
  const copy = useScopedTranslations('booking.review', translations[locale])
  const client = useApolloClient()
  const navigate = useNavigate()
  const [booking, setBooking] = useState<CustomerBooking | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [createdReview, setCreatedReview] = useState<CreatedReview | null>(null)
  const [createReview, { loading: submitting }] = useMutation<CreateReviewData, CreateReviewVariables>(CREATE_REVIEW_MUTATION)

  useEffect(() => {
    let active = true

    async function loadBooking() {
      if (!id) {
        setNotFound(true)
        setLoading(false)
        return
      }

      try {
        let after: string | null = null

        while (active) {
          const response = await client.query<MyBookingsData, MyBookingsVariables>({
            query: MY_BOOKINGS_QUERY,
            variables: { first: 50, after },
            fetchPolicy: 'network-only',
          })
          const queryData = response.data as MyBookingsData | undefined
          if (!queryData) break

          const match = queryData.myBookings.nodes.find((node: CustomerBooking) => node.id === id)

          if (match) {
            setBooking(match)
            setNotFound(false)
            setLoading(false)
            return
          }

          if (!queryData.myBookings.pageInfo.hasNextPage || !queryData.myBookings.pageInfo.endCursor) {
            break
          }

          after = queryData.myBookings.pageInfo.endCursor
        }

        if (active) {
          setNotFound(true)
          setLoading(false)
        }
      } catch {
        if (active) {
          setErrors([copy.notFoundBody])
          setLoading(false)
        }
      }
    }

    void loadBooking()

    return () => {
      active = false
    }
  }, [client, copy.notFoundBody, id])

  useEffect(() => {
    if (!createdReview || !booking) return

    const timeout = window.setTimeout(() => navigate(`/bookings/${booking.id}`, { replace: true }), 3000)
    return () => window.clearTimeout(timeout)
  }, [booking, createdReview, navigate])

  async function handleSubmitReview(input: { rating: number; comment: string }) {
    if (!booking) return [copy.notFoundBody]

    const { data } = await createReview({
      variables: {
        bookingId: booking.id,
        rating: input.rating,
        comment: input.comment || undefined,
      },
    })

    const mutationErrors = data?.createReview.errors ?? [copy.notFoundBody]
    if (mutationErrors.length > 0 || !data?.createReview.review) return mutationErrors

    setCreatedReview(data.createReview.review)
    await client.refetchQueries({ include: [MY_BOOKINGS_QUERY] })
    return []
  }

  if (loading) {
    return <div className="mx-auto max-w-5xl px-4 py-10 text-sm text-slate-500 md:px-6">{copy.loading}</div>
  }

  if (notFound || !booking || booking.status !== 'completed') {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-950">{copy.notFoundTitle}</h1>
          <p className="mt-3 text-sm leading-7 text-slate-600">{copy.notFoundBody}</p>
          <div className="mt-6">
            <Link to={`/bookings/${id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
              <ArrowLeft className="h-4 w-4" />
              {copy.back}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const existingReview = createdReview ?? booking.review

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-10">
      <Link to={`/bookings/${booking.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4" />
        {copy.back}
      </Link>

      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eff6ff_100%)] p-6 shadow-sm md:p-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">{copy.title}</h1>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{copy.body}</p>
      </section>

      <div className="mt-8">
        <FormError errors={errors} />
      </div>

      {existingReview ? (
        <section className="mt-8 rounded-[1.75rem] border border-emerald-100 bg-emerald-50 p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-5 w-5 text-emerald-600" />
            <div>
              <h2 className="text-2xl font-bold text-emerald-950">{copy.successTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-emerald-900">
                {booking.review ? copy.alreadyReviewed : copy.successBody}
              </p>
            </div>
          </div>

          <div className="mt-6 max-w-xl">
            <ReviewCard
              review={{
                ...existingReview,
                reviewer: {
                  firstName: locale === 'el' ? 'Εσύ' : 'You',
                  lastName: '',
                },
              }}
              placeholder={copy.reviewPlaceholder}
            />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/bookings/${booking.id}`} className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700">
              {copy.backToBookings}
            </Link>
            {!booking.review && (
              <p className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-800">
                {copy.redirecting}
              </p>
            )}
          </div>
        </section>
      ) : (
        <div className="mt-8">
          <ReviewForm
            booking={booking}
            locale={locale}
            submitting={submitting}
            onSubmit={handleSubmitReview}
            copy={copy}
          />
        </div>
      )}
    </div>
  )
}
